export const BUSINESS_DOCUMENT_TYPE = {
    QUOTE: 'QUOTE',
    INVOICE: 'INVOICE',
};

export const BUSINESS_STATUS_ENUM = {
    IN_PROGRESS: { label: 'En cours de réalisation', value: "IN_PROGRESS", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE] },
    CREATION: { label: 'En rédaction', value: "CREATION", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE] },
    ACCEPTED: { label: 'Accepté', value: "ACCEPTED", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE] },
    DONE: { label: 'Réalisation terminé', value: "DONE", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE] },
    PAID: { label: 'Payée', value: "PAID", scopes: [BUSINESS_DOCUMENT_TYPE.INVOICE] },
    PAID_AND_DECLARED: { label: 'Payée et déclarée', value: "PAID_AND_DECLARED", scopes: [BUSINESS_DOCUMENT_TYPE.INVOICE] },
    REFUSED: { label: 'Refusé', value: "REFUSED", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE] },
    SENT: { label: 'Envoyé', value: "SENT", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE, BUSINESS_DOCUMENT_TYPE.INVOICE] },
    READY_TO_SEND: { label: 'Prêt à l\'envoie', value: "READY_TO_SEND", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE, BUSINESS_DOCUMENT_TYPE.INVOICE] },
    ABORTED: { label: 'Annulé', value: "ABORTED", scopes: [BUSINESS_DOCUMENT_TYPE.QUOTE, BUSINESS_DOCUMENT_TYPE.INVOICE] },
}

export const BUSINESS_STATUS_ENUM_ARRAY = [
    BUSINESS_STATUS_ENUM.IN_PROGRESS,
    BUSINESS_STATUS_ENUM.CREATION,
    BUSINESS_STATUS_ENUM.ACCEPTED,
    BUSINESS_STATUS_ENUM.DONE,
    BUSINESS_STATUS_ENUM.PAID,
    BUSINESS_STATUS_ENUM.PAID_AND_DECLARED,
    BUSINESS_STATUS_ENUM.REFUSED,
    BUSINESS_STATUS_ENUM.SENT,
    BUSINESS_STATUS_ENUM.READY_TO_SEND,
    BUSINESS_STATUS_ENUM.ABORTED,
]

// Statuts "de sortie" : jamais sur la ligne principale, ce sont des issues
// possibles depuis plusieurs étapes (pas un enchaînement).
export const TERMINAL_BUSINESS_STATUSES = [BUSINESS_STATUS_ENUM.REFUSED, BUSINESS_STATUS_ENUM.ABORTED];

// Statuts "payé" d'une facture.
export const PAID_BUSINESS_STATUSES = [BUSINESS_STATUS_ENUM.PAID, BUSINESS_STATUS_ENUM.PAID_AND_DECLARED];

export function isTerminalBusinessStatus(status) {
    return TERMINAL_BUSINESS_STATUSES.some(terminalStatus => terminalStatus.value === status);
}

// Enchaînement "normal" de chaque type de document, déduit de
// possibleBusinessStatusSelect() : sert uniquement à l'affichage (une
// frise, une barre de progression...), jamais à décider ce qui est permis.
export const BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE = {
    [BUSINESS_DOCUMENT_TYPE.QUOTE]: [
        BUSINESS_STATUS_ENUM.CREATION,
        BUSINESS_STATUS_ENUM.READY_TO_SEND,
        BUSINESS_STATUS_ENUM.SENT,
        BUSINESS_STATUS_ENUM.ACCEPTED,
        BUSINESS_STATUS_ENUM.IN_PROGRESS,
        BUSINESS_STATUS_ENUM.DONE,
    ],
    [BUSINESS_DOCUMENT_TYPE.INVOICE]: [
        BUSINESS_STATUS_ENUM.READY_TO_SEND,
        BUSINESS_STATUS_ENUM.SENT,
        BUSINESS_STATUS_ENUM.PAID,
        BUSINESS_STATUS_ENUM.PAID_AND_DECLARED,
    ],
};

export function getBusinessDocumentType(business) {
    return business.invoiceNumber ? BUSINESS_DOCUMENT_TYPE.INVOICE : BUSINESS_DOCUMENT_TYPE.QUOTE;
}

// Devis envoyé dont la signature a été demandée mais pas encore signée : il passera automatiquement à
// ACCEPTED quand le client signe (ACCEPTED n'est donc pas une transition manuelle dans ce cas).
export function isQuoteAwaitingSignature(business) {
    return !business.invoiceNumber
        && business.status === BUSINESS_STATUS_ENUM.SENT.value
        && !!business.signature
        && !business.signature.signedAt;
}

// ------------------------------------------------------------
// Logique de transition réelle du statut Business (devis/facture) : pour le statut courant
// d'un business, quels statuts sont réellement atteignables.
// ------------------------------------------------------------
export function possibleBusinessStatusSelect(business) {

    // Un business annulé est sorti du workflow : aucune transition, ni étape normale ni retour arrière.
    if (business.status === BUSINESS_STATUS_ENUM.ABORTED.value) {
        return [BUSINESS_STATUS_ENUM.ABORTED];
    }

    const values = [];
    const status = business.status;
    const isInvoice = !!business.invoiceNumber;
    const isQuote = !isInvoice;

    // CREATION
    if(isQuote && (status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value || status === BUSINESS_STATUS_ENUM.SENT.value)) {
        values.push(BUSINESS_STATUS_ENUM.CREATION);
    }

    // SENT on invoice
    if(isInvoice && status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value) {
        values.push(BUSINESS_STATUS_ENUM.SENT);
    }

    // SENT on quote
    if(isQuote && status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value) {
        values.push(BUSINESS_STATUS_ENUM.SENT);
    }

    // READY_TO_SEND
    if(isQuote && status === BUSINESS_STATUS_ENUM.CREATION.value) {
        values.push(BUSINESS_STATUS_ENUM.READY_TO_SEND);
    }

    // ABORTED
    if(status !== BUSINESS_STATUS_ENUM.PAID.value){
        values.push(BUSINESS_STATUS_ENUM.ABORTED);
    }

    // IN_PROGRESS
    if(isQuote && status === BUSINESS_STATUS_ENUM.ACCEPTED.value) {
        values.push(BUSINESS_STATUS_ENUM.IN_PROGRESS);
    }

    // ACCEPTED
    if(isQuote && status === BUSINESS_STATUS_ENUM.SENT.value) {
        // Signature case : signature en attente = acceptation automatique à la signature (voir StatusSection)
        if(!isQuoteAwaitingSignature(business)){
            values.push(BUSINESS_STATUS_ENUM.ACCEPTED);
        }
    }

    // DONE
    if(isQuote && status === BUSINESS_STATUS_ENUM.IN_PROGRESS.value) {
        values.push(BUSINESS_STATUS_ENUM.DONE);
    }

    // REFUSED
    if(isQuote && status === BUSINESS_STATUS_ENUM.SENT.value) {
        values.push(BUSINESS_STATUS_ENUM.REFUSED);
    }

    // PAID
    if(isInvoice && status === BUSINESS_STATUS_ENUM.SENT.value) {
        values.push(BUSINESS_STATUS_ENUM.PAID);
    }

    // PAID_AND_DECLARED
    if(isInvoice && status === BUSINESS_STATUS_ENUM.PAID.value) {
        values.push(BUSINESS_STATUS_ENUM.PAID_AND_DECLARED);
    }

    // Current status
    values.push(BUSINESS_STATUS_ENUM[status]);

    return values;
}

// ------------------------------------------------------------
// Transitions vues depuis l'onglet consulté (documentType : BUSINESS_DOCUMENT_TYPE.QUOTE / INVOICE,
// qui peut différer du type réel du business), en distinguant trois choses :
//  - savedStatus    : le dernier statut réel connu du document consulté (business.status, jamais la sélection en cours) ;
//  - selectedStatus : la sélection en cours (non enregistrée) ;
//  - pendingStatus  : la sélection en cours si elle diffère du statut réel (étape suivante OU issue), sinon undefined.
// RÈGLE : `transitions` ne dépend que du statut réel (business). La sélection n'influence jamais
// le workflow : sélectionner une étape ou une issue (Annulé...) ne retire ni ne recalcule aucune option,
// elle est seulement exposée via selectedStatus / pendingStatus pour l'affichage et la soumission.
// Onglet Devis d'un business qui est déjà une facture (invoiceNumber) : le devis est définitivement
// terminé (DONE), quel que soit business.status. Aucune transition, ni étape ni issue (pas d'Annulé).
// ------------------------------------------------------------
export function getBusinessStatusTransitions(business, selectedStatus, documentType) {
    const isQuoteViewOfInvoice = documentType === BUSINESS_DOCUMENT_TYPE.QUOTE && !!business.invoiceNumber;

    // Devis dont une facture a été générée : figé à "Réalisation terminée", rien n'est sélectionnable.
    if (isQuoteViewOfInvoice) {
        const doneStatus = BUSINESS_STATUS_ENUM.DONE.value;
        return {savedStatus: doneStatus, selectedStatus: doneStatus, pendingStatus: undefined, transitions: [BUSINESS_STATUS_ENUM.DONE]};
    }

    const savedStatus = business.status;
    const pendingStatus = (selectedStatus && selectedStatus !== savedStatus) ? selectedStatus : undefined;

    // Transitions uniquement issues du statut réel.
    const transitions = possibleBusinessStatusSelect(business);
    // Le statut réel reste toujours sélectionnable (pour annuler une sélection en cours).
    if (!transitions.some(option => option.value === savedStatus)) {
        transitions.push(BUSINESS_STATUS_ENUM[savedStatus]);
    }

    return {savedStatus, selectedStatus: selectedStatus, pendingStatus, transitions};
}